import { useEffect, useMemo, useRef, useState } from 'react';
import { BOARD_GAP, computeBoardTileSize } from '../boardLayout';
import MulnyangGameHud from './MulnyangGameHud';
import useMulnyangGameAudio from './useMulnyangGameAudio';
import adRewardIcon from '../../../assets/icons/mulnyang/ad-reward-icon.png';
import streakFireIcon from '../../../assets/icons/mulnyang/streak-fire.png';
import streakGlowImage from '../../../assets/images/mulnyang-bulnyang/streak-glow-figma.png';

const BASE_CLICK_LIMIT = 7;
const WRONG_FEEDBACK_MS = 420;
const SUCCESS_COPY_BY_DIFFICULTY = {
  easy: { title: '정답!', adCopy: '광고 시청시 포인트 x 2' },
  normal: { title: '나이스!', adCopy: '광고 시청시 포인트 x 2' },
  hard: { title: '대단해!', adCopy: '' },
  hell: { title: '퍼펙트!', adCopy: '' },
};
const STREAK_REWARD_BY_DIFFICULTY = {
  normal: 8,
  hard: 30,
  hell: 60,
};
const RETRY_LIMIT_BY_DIFFICULTY = {
  easy: 1,
  normal: 1,
  hard: 2,
  hell: 2,
};
const getInitialClickLimit = (board) => {
  const serverLimit = Number(board?.maxWrongClicks);
  return Number.isFinite(serverLimit) && serverLimit > 0 ? serverLimit : BASE_CLICK_LIMIT;
};
const STREAK_AREA_HEIGHT = 171;

export default function MulnyangGameBoardOverlay({
  board,
  headerArrow,
  onClose,
  onContinue,
  successStreakCount = 0,
  onChangeSuccessStreakCount,
  snackBalance = 0,
  pendingStreakRewardPoint = 0,
  onToast,
  isExternallyPaused = false,
  isMockMode = true,
  onPlayClear = null,
  onRetryTime = null,
  onRetryClick = null,
  onPlayFail = null,
  onPlayForfeit = null,
  onForfeitPendingRewards = null,
  onRunRewardAd = null,
  isMulnyangAdLoading = false,
}) {
  const [wrongClickCount, setWrongClickCount] = useState(0);
  const [wrongCellId, setWrongCellId] = useState(null);
  const [isSuccessPopupOpen, setIsSuccessPopupOpen] = useState(false);
  const [popupKind, setPopupKind] = useState('success');
  const [popupQueue, setPopupQueue] = useState([]);
  const [solvedCell, setSolvedCell] = useState(null);
  const [roundDurationSeconds, setRoundDurationSeconds] = useState(board?.limitSeconds ?? 0);
  const [currentClickLimit, setCurrentClickLimit] = useState(getInitialClickLimit(board));
  const [timeRetryUsed, setTimeRetryUsed] = useState(0);
  const [clickRetryUsed, setClickRetryUsed] = useState(0);
  const [retryTimeRemaining, setRetryTimeRemaining] = useState(null);
  const [retryClickRemaining, setRetryClickRemaining] = useState(null);
  const [localSnackCount, setLocalSnackCount] = useState(snackBalance);
  const [roundResetTick, setRoundResetTick] = useState(0);
  const [isPopupActionPending, setIsPopupActionPending] = useState(false);
  const [isRoundFailedFinalized, setIsRoundFailedFinalized] = useState(false);
  const [gameOverFailReason, setGameOverFailReason] = useState(null);
  const [lastClearPointGranted, setLastClearPointGranted] = useState(null);
  const [lastPendingClearBonusPoint, setLastPendingClearBonusPoint] = useState(null);
  const [lastAdBonusGranted, setLastAdBonusGranted] = useState(null);
  const wrongFeedbackTimerRef = useRef(null);
  const timeoutGuardUntilRef = useRef(0);
  const boardPlayAreaRef = useRef(null);
  const [tileSize, setTileSize] = useState(60);
  const { playWrongSound, playClearSfx, playStreakSfx, playCoinSfx } = useMulnyangGameAudio();
  const boardId = board?.boardId;
  const boardLimitSeconds = board?.limitSeconds ?? 0;
  const boardResetKey = useMemo(() => `${boardId ?? 'board'}-${roundResetTick}`, [boardId, roundResetTick]);

  useEffect(() => {
    return () => {
      if (wrongFeedbackTimerRef.current) {
        window.clearTimeout(wrongFeedbackTimerRef.current);
        wrongFeedbackTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    setLocalSnackCount(snackBalance);
  }, [snackBalance]);

  const difficultyKey = board?.difficultyKey ?? 'easy';
  const successCopy = SUCCESS_COPY_BY_DIFFICULTY[difficultyKey] ?? SUCCESS_COPY_BY_DIFFICULTY.easy;
  const streakReward = STREAK_REWARD_BY_DIFFICULTY[difficultyKey] ?? 8;
  const streakRewardDisplay =
    Number.isFinite(Number(pendingStreakRewardPoint)) && Number(pendingStreakRewardPoint) > 0
      ? Number(pendingStreakRewardPoint)
      : streakReward;
  const retryLimit = RETRY_LIMIT_BY_DIFFICULTY[difficultyKey] ?? 1;
  const streakDisplayCount = successStreakCount > 0 ? ((successStreakCount - 1) % 3) + 1 : 0;
  const shouldShowStreakIndicator = difficultyKey !== 'easy';

  useEffect(() => {
    const playArea = boardPlayAreaRef.current;
    if (!playArea || !board?.cols || !board?.rows) return undefined;

    const updateTileSize = () => {
      const { width, height } = playArea.getBoundingClientRect();
      const isCompactDifficulty = difficultyKey === 'easy' || difficultyKey === 'normal';
      setTileSize(
        computeBoardTileSize({
          width,
          height: isCompactDifficulty ? 0 : height,
          cols: board.cols,
          rows: board.rows,
          reserveBottom: shouldShowStreakIndicator ? STREAK_AREA_HEIGHT + 16 : 0,
        }),
      );
    };

    updateTileSize();
    const observer = new ResizeObserver(updateTileSize);
    observer.observe(playArea);
    return () => observer.disconnect();
  }, [board?.cols, board?.rows, boardId, difficultyKey, shouldShowStreakIndicator]);

  useEffect(() => {
    if (!boardId) return;
    setRoundDurationSeconds(boardLimitSeconds);
    setCurrentClickLimit(getInitialClickLimit(board));
    setWrongClickCount(0);
    setWrongCellId(null);
    setTimeRetryUsed(Number.isFinite(Number(board?.failRetryTimeUsed)) ? Number(board.failRetryTimeUsed) : 0);
    setClickRetryUsed(Number.isFinite(Number(board?.failRetryClickUsed)) ? Number(board.failRetryClickUsed) : 0);
    setRetryTimeRemaining(
      Number.isFinite(Number(board?.retryTimeRemaining)) ? Number(board.retryTimeRemaining) : null,
    );
    setRetryClickRemaining(
      Number.isFinite(Number(board?.retryClickRemaining)) ? Number(board.retryClickRemaining) : null,
    );
    setRoundResetTick(0);
    setIsRoundFailedFinalized(false);
    setGameOverFailReason(null);
    setLastClearPointGranted(null);
    setLastPendingClearBonusPoint(null);
    setLastAdBonusGranted(null);
    setPopupQueue([]);
  }, [board, boardId, boardLimitSeconds]);

  const canRetryTimeOver =
    Number.isFinite(Number(retryTimeRemaining)) && !isMockMode
      ? Number(retryTimeRemaining) > 0
      : timeRetryUsed < retryLimit;
  const canRetryClickOver =
    Number.isFinite(Number(retryClickRemaining)) && !isMockMode
      ? Number(retryClickRemaining) > 0
      : clickRetryUsed < retryLimit;
  const isRetryExhaustedState =
    (popupKind === 'timeout' && !canRetryTimeOver) || (popupKind === 'click-over' && !canRetryClickOver);

  useEffect(() => {
    if (!board) return;
    if (!isSuccessPopupOpen) return;
    if (!(popupKind === 'timeout' || popupKind === 'click-over')) return;
    if (isRoundFailedFinalized) return;

    const failReason = popupKind === 'timeout' ? 'timeout' : 'clickOver';
    let isMounted = true;
    (async () => {
      try {
        const payload = await callPlayFail(failReason);
        applyRetryResponse(payload);
        if (!isMounted) return;

        const serverRetryTimeRemaining = Number(payload?.retryTimeRemaining);
        const serverRetryClickRemaining = Number(payload?.retryClickRemaining);
        const hasServerRetryTimeRemaining = Number.isFinite(serverRetryTimeRemaining);
        const hasServerRetryClickRemaining = Number.isFinite(serverRetryClickRemaining);

        const shouldShowGameOver =
          failReason === 'timeout'
            ? (hasServerRetryTimeRemaining ? serverRetryTimeRemaining <= 0 : !canRetryTimeOver)
            : (hasServerRetryClickRemaining ? serverRetryClickRemaining <= 0 : !canRetryClickOver);

        if (shouldShowGameOver) {
          setGameOverFailReason(failReason);
          setPopupKind('game-over');
        }
        setIsRoundFailedFinalized(true);
      } catch (error) {
        if (!isMounted) return;
        onToast?.(error?.message || '실패 처리에 실패했어요. 잠시 후 다시 시도해주세요.');
        setIsRoundFailedFinalized(true);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [board, isSuccessPopupOpen, popupKind, isRoundFailedFinalized, canRetryTimeOver, canRetryClickOver]);

  if (!board) return null;

  const handleTileClick = async (cell) => {
    if (isExternallyPaused) return;
    if (isSuccessPopupOpen) return;
    if (isPopupActionPending) return;
    if (cell.isOdd) {
      if (!isMockMode) {
        setIsPopupActionPending(true);
        try {
          const clearPayload = await callPlayClear();
          if (Number.isFinite(Number(clearPayload?.clearPointGranted))) {
            setLastClearPointGranted(Number(clearPayload.clearPointGranted));
          }
          if (Number.isFinite(Number(clearPayload?.pendingClearBonusPoint))) {
            setLastPendingClearBonusPoint(Number(clearPayload.pendingClearBonusPoint));
          }
        } catch (error) {
          onToast?.(error?.message || '클리어 처리에 실패했어요. 잠시 후 다시 시도해주세요.');
          setIsPopupActionPending(false);
          return;
        }
        setIsPopupActionPending(false);
      }
      const nextStreakCount = successStreakCount + 1;
      const shouldOpenStreakPopup = board.difficultyKey !== 'easy' && nextStreakCount % 3 === 0;
      if (shouldOpenStreakPopup) playStreakSfx();
      else playClearSfx(board.difficultyKey ?? 'easy');
      if (board.difficultyKey !== 'easy') {
        onChangeSuccessStreakCount?.(nextStreakCount);
      }
      setPopupKind(shouldOpenStreakPopup ? 'streak' : 'success');
      setPopupQueue(shouldOpenStreakPopup ? ['success'] : []);
      setSolvedCell(cell);
      setIsSuccessPopupOpen(true);
      return;
    }

    const nextWrongCount = Math.min(currentClickLimit, wrongClickCount + 1);
    setWrongClickCount(nextWrongCount);
    setWrongCellId(null);
    window.requestAnimationFrame(() => {
      setWrongCellId(cell.id);
    });
    playWrongSound();

    if (nextWrongCount >= currentClickLimit) {
      setIsRoundFailedFinalized(false);
      setGameOverFailReason(null);
      setPopupKind('click-over');
      setIsSuccessPopupOpen(true);
    }

    if (wrongFeedbackTimerRef.current) {
      window.clearTimeout(wrongFeedbackTimerRef.current);
    }
    wrongFeedbackTimerRef.current = window.setTimeout(() => {
      setWrongCellId(null);
      wrongFeedbackTimerRef.current = null;
    }, WRONG_FEEDBACK_MS);
  };

  const handleTimeUp = () => {
    if (Date.now() < timeoutGuardUntilRef.current) return false;
    if (isSuccessPopupOpen) return false;
    setIsRoundFailedFinalized(false);
    setGameOverFailReason(null);
    setPopupKind('timeout');
    setIsSuccessPopupOpen(true);
    return true;
  };

  const getTimeRetrySeconds = () => (timeRetryUsed === 0 ? 5 : 3);
  const isSnackAvailable = localSnackCount > 0;

  const timeoutRetryCta = isSnackAvailable
    ? `간식 1개 쓰고 +${getTimeRetrySeconds()}초 추가 도전`
    : `광고 보고 +${getTimeRetrySeconds()}초 추가 도전`;
  const clickRetryCta = isSnackAvailable ? '간식 1개 쓰고 +3 클릭 추가 도전' : '광고 보고 +3 클릭 추가 도전';
  const canRetryCurrent =
    popupKind === 'timeout' ? canRetryTimeOver : popupKind === 'click-over' ? canRetryClickOver : true;

  const consumeSnackIfNeeded = () => {
    if (!isSnackAvailable) return;
    setLocalSnackCount((prev) => Math.max(0, prev - 1));
  };

  const resetRoundState = () => {
    setWrongClickCount(0);
    setWrongCellId(null);
    setIsSuccessPopupOpen(false);
    setPopupKind('success');
    setPopupQueue([]);
    setSolvedCell(null);
    setLastAdBonusGranted(null);
    setRoundResetTick((prev) => prev + 1);
  };

  const openNextPopupOrContinue = async () => {
    if (popupQueue.length > 0) {
      const [nextPopupKind, ...restQueue] = popupQueue;
      setPopupQueue(restQueue);
      setPopupKind(nextPopupKind);
      return;
    }
    const continueResult = await onContinue?.();
    if (continueResult === false) return;
    setIsSuccessPopupOpen(false);
    setPopupKind('success');
    setSolvedCell(null);
  };

  const isStreakWithQueuedClearPopup = popupKind === 'streak' && popupQueue.length > 0;

  const handleSkipStreakPopup = async () => {
    if (isPopupActionPending) return;
    setIsPopupActionPending(true);
    try {
      if (!isMockMode) {
        await callForfeitPendingRewards('streak');
      }
      onChangeSuccessStreakCount?.(0);
      await openNextPopupOrContinue();
    } catch (error) {
      onToast?.(error?.message || '처리에 실패했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setIsPopupActionPending(false);
    }
  };

  const handlePopupClose = () => {
    if (isStreakWithQueuedClearPopup) {
      handleSkipStreakPopup();
      return;
    }
    handleExitToLobby();
  };

  const handleRetryTimeOver = () => {
    if (isMockMode && !canRetryTimeOver) return;
    if (!isMockMode) {
      timeoutGuardUntilRef.current = Date.now() + 700;
      setWrongCellId(null);
      setIsSuccessPopupOpen(false);
      setPopupKind('success');
      setSolvedCell(null);
      setRoundResetTick((prev) => prev + 1);
      setIsRoundFailedFinalized(false);
      setGameOverFailReason(null);
      return;
    }
    const retrySeconds = getTimeRetrySeconds();
    timeoutGuardUntilRef.current = Date.now() + 700;
    consumeSnackIfNeeded();
    setRoundDurationSeconds(retrySeconds);
    setTimeRetryUsed((prev) => prev + 1);
    resetRoundState();
  };

  const handleRetryClickOver = () => {
    if (isMockMode && !canRetryClickOver) return;
    if (!isMockMode) {
      setWrongCellId(null);
      setIsSuccessPopupOpen(false);
      setPopupKind('success');
      setSolvedCell(null);
      setRoundResetTick((prev) => prev + 1);
      setIsRoundFailedFinalized(false);
      setGameOverFailReason(null);
      return;
    }
    consumeSnackIfNeeded();
    setCurrentClickLimit(3);
    setClickRetryUsed((prev) => prev + 1);
    resetRoundState();
  };

  const getAdBonusPoint = () => {
    if (Number.isFinite(Number(lastPendingClearBonusPoint))) {
      return Number(lastPendingClearBonusPoint);
    }
    const clearPoint = Number(board?.clearPoint ?? 0);
    if (difficultyKey === 'easy' || difficultyKey === 'normal') return clearPoint;
    return 0;
  };
  const clearPointDisplay = Number.isFinite(Number(lastClearPointGranted))
    ? Number(lastClearPointGranted)
    : Number(board?.clearPoint ?? 0);
  const adBonusDisplay = Number.isFinite(Number(lastAdBonusGranted)) ? Number(lastAdBonusGranted) : getAdBonusPoint();

  const callRetryTime = async (adToken = null) => {
    if (isMockMode || typeof onRetryTime !== 'function') return true;
    if (!board?.roundToken) return false;
    return onRetryTime({
      roundToken: board.roundToken,
      wrongClickCount,
      ...(adToken ? { adToken } : {}),
    });
  };

  const callRetryClick = async (adToken = null) => {
    if (isMockMode || typeof onRetryClick !== 'function') return true;
    if (!board?.roundToken) return false;
    return onRetryClick({
      roundToken: board.roundToken,
      wrongClickCount,
      ...(adToken ? { adToken } : {}),
    });
  };

  const callRunRewardAd = async (placement) => {
    if (isMockMode || typeof onRunRewardAd !== 'function') return { placement, adToken: null, completePayload: null };
    return onRunRewardAd(placement);
  };

  const callPlayForfeit = async () => {
    if (isMockMode || typeof onPlayForfeit !== 'function') return true;
    if (!board?.roundToken) return false;
    await onPlayForfeit({
      roundToken: board.roundToken,
      wrongClickCount,
    });
    return true;
  };

  const callPlayFail = async (failReason) => {
    if (isMockMode || typeof onPlayFail !== 'function') return true;
    if (!board?.roundToken) return false;
    return onPlayFail({
      roundToken: board.roundToken,
      wrongClickCount,
      failReason,
    });
  };

  const callForfeitPendingRewards = async (target = 'all') => {
    if (isMockMode || typeof onForfeitPendingRewards !== 'function') return true;
    await onForfeitPendingRewards(target);
    return true;
  };

  const callPlayClear = async () => {
    if (isMockMode || typeof onPlayClear !== 'function') return true;
    if (!board?.roundToken || !board?.serverAnswer) return false;
    return onPlayClear({
      roundToken: board.roundToken,
      answerRow: board.serverAnswer.answerRow,
      answerCol: board.serverAnswer.answerCol,
      wrongClickCount,
    });
  };

  const applyRetryResponse = (payload) => {
    if (!payload || payload === true) return;
    if (Number.isFinite(Number(payload?.failRetryTimeUsed))) setTimeRetryUsed(Number(payload.failRetryTimeUsed));
    if (Number.isFinite(Number(payload?.failRetryClickUsed))) setClickRetryUsed(Number(payload.failRetryClickUsed));
    if (Number.isFinite(Number(payload?.wrongClickCount))) setWrongClickCount(Number(payload.wrongClickCount));
    if (Number.isFinite(Number(payload?.maxWrongClicks)) && Number(payload.maxWrongClicks) > 0) {
      setCurrentClickLimit(Number(payload.maxWrongClicks));
    }
    if (Number.isFinite(Number(payload?.limitSec)) && Number(payload.limitSec) > 0) {
      setRoundDurationSeconds(Number(payload.limitSec));
    }
    if (Number.isFinite(Number(payload?.snackCount)) && Number(payload.snackCount) >= 0) {
      setLocalSnackCount(Number(payload.snackCount));
    }
    if (Number.isFinite(Number(payload?.retryTimeRemaining))) {
      setRetryTimeRemaining(Number(payload.retryTimeRemaining));
    }
    if (Number.isFinite(Number(payload?.retryClickRemaining))) {
      setRetryClickRemaining(Number(payload.retryClickRemaining));
    }
  };

  const handleExitToLobby = async () => {
    if (isPopupActionPending) return;
    if (isStreakWithQueuedClearPopup) {
      await handleSkipStreakPopup();
      return;
    }
    setIsPopupActionPending(true);
    try {
      if (isSuccessPopupOpen && (popupKind === 'success' || popupKind === 'streak')) {
        await callForfeitPendingRewards('all');
      }
      const isRewardPopup = isSuccessPopupOpen && (popupKind === 'success' || popupKind === 'bonus' || popupKind === 'streak');
      const isFailurePopup = isSuccessPopupOpen && (popupKind === 'timeout' || popupKind === 'click-over' || popupKind === 'game-over');
      if (board?.roundToken && !isRewardPopup && (!isRoundFailedFinalized || isFailurePopup)) {
        await callPlayForfeit();
      }
      onClose?.();
    } catch (error) {
      onToast?.(error?.message || '처리에 실패했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setIsPopupActionPending(false);
    }
  };

  return (
    <section className="mulnyang-game-overlay">
      <header className="mulnyang-game-header">
        <button type="button" className="mulnyang-back" onClick={handleExitToLobby} disabled={isPopupActionPending}>
          <img src={headerArrow} alt="" />
        </button>
      </header>

      <MulnyangGameHud
        durationSeconds={roundDurationSeconds}
        wrongClickCount={wrongClickCount}
        clickLimit={currentClickLimit}
        resetKey={boardResetKey}
        isPaused={isSuccessPopupOpen || isExternallyPaused}
        onTimeUp={handleTimeUp}
      />

      <div className={`mulnyang-game-play-area mulnyang-game-play-area--${difficultyKey}`} ref={boardPlayAreaRef}>
        <div
          className="mulnyang-game-board"
          style={{
            gridTemplateColumns: `repeat(${board.cols}, ${tileSize}px)`,
            '--tile-size': `${tileSize}px`,
            '--board-gap': `${BOARD_GAP}px`,
          }}
        >
          {board.cells.map((cell) => {
            const isWrongFeedback = wrongCellId === cell.id;
            return (
              <button
                key={cell.id}
                type="button"
                className={`mulnyang-game-tile${cell.isOdd ? ' odd' : ''}${isWrongFeedback ? ' wrong' : ''}`}
                onClick={() => handleTileClick(cell)}
              >
                <img className="tile-bg" src={cell.tileSrc} alt="" />
                <img
                  className={`tile-overlay${board.contentType === 'letter' ? ' is-letter' : ''}`}
                  src={cell.overlaySrc}
                  alt=""
                />
                {isWrongFeedback ? (
                  <span className="tile-wrong-mark" aria-hidden="true">
                    ❌
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>

        {shouldShowStreakIndicator ? (
          <div className="mulnyang-streak-indicator-wrap" aria-live="polite">
            <img className="streak-glow-image" src={streakGlowImage} alt="" aria-hidden="true" />
            <div className="mulnyang-streak-indicator">
              <span>{`”${streakDisplayCount}” 연승`}</span>
            </div>
          </div>
        ) : null}
      </div>

      {isSuccessPopupOpen ? (
        <div
          className={`mulnyang-success-popup-dim${popupKind === 'streak' ? ' is-streak' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-label="게임 팝업"
        >
          {popupKind === 'streak' ? (
            <div className="streak-celebration-bg" aria-hidden="true">
              {Array.from({ length: 42 }).map((_, i) => (
                <span
                  key={`streak-fall-${i}`}
                  className="streak-fall-particle"
                  style={{
                    left: `${(i * 17) % 100}%`,
                    animationDelay: `${(i % 9) * 0.22}s`,
                    animationDuration: `${2.4 + (i % 5) * 0.28}s`,
                  }}
                />
              ))}
              {Array.from({ length: 28 }).map((_, i) => {
                const angle = (Math.PI * 2 * i) / 28;
                const dist = 56 + (i % 7) * 18;
                return (
                  <span
                    key={`streak-burst-${i}`}
                    className="streak-burst-particle"
                    style={{
                      '--sx': `${Math.cos(angle) * dist}px`,
                      '--sy': `${Math.sin(angle) * dist}px`,
                      animationDelay: `${(i % 8) * 0.06}s`,
                    }}
                  />
                );
              })}
            </div>
          ) : null}
          <div className="mulnyang-success-popup">
            <button
              type="button"
              className="popup-close"
              aria-label={isStreakWithQueuedClearPopup ? '3연승 보상 건너뛰기' : '팝업 닫기'}
              onClick={handlePopupClose}
              disabled={isPopupActionPending}
            >
              ×
            </button>
            {popupKind === 'game-over' ? (
              <>
                <div className="timeout-emoji">😿</div>
                <h3>게임 오버</h3>
                <p className="timeout-copy">아쉽지만 재도전 해보자냥</p>
              </>
            ) : popupKind === 'timeout' ? (
              <>
                <div className="timeout-emoji">⏰🙀</div>
                <h3>앗, 시간 초과</h3>
                <p className="timeout-copy">아깝다냥! 한번 더 도전해볼까?</p>
              </>
            ) : popupKind === 'click-over' ? (
              <>
                <div className="timeout-emoji">⏰🙀</div>
                <h3>{currentClickLimit === BASE_CLICK_LIMIT ? '앗, 7회 클릭 초과' : '앗, 추가 3회 클릭 초과'}</h3>
                <p className="timeout-copy">아깝다냥! 한번 더 도전해볼까?</p>
              </>
            ) : popupKind === 'bonus' ? (
              <>
                <div className="timeout-emoji">🎁</div>
                <h3>추가 보너스 획득!</h3>
                <p className="point-copy">{`+${adBonusDisplay.toLocaleString()}P 추가 획득!`}</p>
              </>
            ) : popupKind === 'streak' ? (
              <>
                <img className="streak-fire-icon" src={streakFireIcon} alt="" />
                <h3>3연승 달성</h3>
                <p className="point-copy">{`+${Number(streakRewardDisplay).toLocaleString()}P`}</p>
              </>
            ) : (
              <>
                <div className="answer-tile">
                  {solvedCell ? (
                    <>
                      <img className="tile-bg" src={solvedCell.tileSrc} alt="" />
                      <img
                        className={`tile-overlay${board.contentType === 'letter' ? ' is-letter' : ''}`}
                        src={solvedCell.overlaySrc}
                        alt="정답 타일"
                      />
                    </>
                  ) : null}
                </div>
                <h3>{successCopy.title}</h3>
                <p className="point-copy">{`+${clearPointDisplay.toLocaleString()}P 획득!`}</p>
              </>
            )}
            {popupKind === 'timeout' ||
            popupKind === 'click-over' ||
            popupKind === 'streak' ||
            (popupKind === 'success' && lastPendingClearBonusPoint > 0) ? (
              <button
                type="button"
                className="action ad"
                disabled={!canRetryCurrent || isPopupActionPending || isMulnyangAdLoading}
                onClick={async () => {
                  if (isPopupActionPending || isMulnyangAdLoading) return;
                  setIsPopupActionPending(true);
                  try {
                    if (popupKind === 'timeout') {
                      if (!isMockMode) {
                        if (!isSnackAvailable) {
                          const adResult = await callRunRewardAd('retryTime');
                          applyRetryResponse(adResult?.completePayload);
                        } else {
                          const payload = await callRetryTime(null);
                          applyRetryResponse(payload);
                        }
                      }
                      handleRetryTimeOver();
                      setIsPopupActionPending(false);
                      return;
                    }
                    if (popupKind === 'click-over') {
                      if (!isMockMode) {
                        if (!isSnackAvailable) {
                          const adResult = await callRunRewardAd('retryClick');
                          applyRetryResponse(adResult?.completePayload);
                        } else {
                          const payload = await callRetryClick(null);
                          applyRetryResponse(payload);
                        }
                      }
                      handleRetryClickOver();
                      setIsPopupActionPending(false);
                      return;
                    }
                    if (popupKind === 'streak') {
                      let adGrantedPoint = null;
                      if (!isMockMode) {
                        const adResult = await callRunRewardAd('streakReward');
                        if (Number.isFinite(Number(adResult?.completePayload?.grantedPoint))) {
                          adGrantedPoint = Number(adResult.completePayload.grantedPoint);
                        }
                      }
                      const total = Number.isFinite(Number(adGrantedPoint))
                        ? Number(adGrantedPoint)
                        : Number(clearPointDisplay ?? 0) + Number(streakReward ?? 0);
                      playCoinSfx();
                      onToast?.(`🔥 3연속 승리로 총 ${total.toLocaleString()}P 획득!`);
                      onChangeSuccessStreakCount?.(0);
                      await openNextPopupOrContinue();
                      setIsPopupActionPending(false);
                      return;
                    }
                    if (popupKind === 'success') {
                      let adGrantedPoint = null;
                      if (!isMockMode) {
                        const adResult = await callRunRewardAd('clearBonus');
                        if (Number.isFinite(Number(adResult?.completePayload?.grantedPoint))) {
                          adGrantedPoint = Number(adResult.completePayload.grantedPoint);
                        }
                      }
                      const bonus = Number.isFinite(Number(adGrantedPoint))
                        ? Number(adGrantedPoint)
                        : getAdBonusPoint();
                      setLastAdBonusGranted(bonus);
                      setPopupKind('bonus');
                    }
                  } catch (error) {
                    onToast?.(error?.message || '처리에 실패했어요. 잠시 후 다시 시도해주세요.');
                  } finally {
                    setIsPopupActionPending(false);
                  }
                }}
              >
                {popupKind === 'timeout' || popupKind === 'click-over' ? null : (
                  <img className="ad-icon" src={adRewardIcon} alt="" aria-hidden="true" />
                )}
                {isPopupActionPending || isMulnyangAdLoading
                  ? '로딩 중...'
                  : popupKind === 'timeout'
                    ? timeoutRetryCta
                    : popupKind === 'click-over'
                      ? clickRetryCta
                      : popupKind === 'streak'
                        ? '광고 시청하고 포인트 획득'
                        : successCopy.adCopy}
              </button>
            ) : null}
            {popupKind === 'success' ? (
              <button
                type="button"
                className="action next"
                disabled={isPopupActionPending}
                onClick={async () => {
                  if (isPopupActionPending) return;
                  setIsPopupActionPending(true);
                  try {
                    await callForfeitPendingRewards('clearBonus');
                    await openNextPopupOrContinue();
                  } catch (error) {
                    onToast?.(error?.message || '처리에 실패했어요. 잠시 후 다시 시도해주세요.');
                  } finally {
                    setIsPopupActionPending(false);
                  }
                }}
              >
                다음 이어하기
              </button>
            ) : null}
            {popupKind === 'bonus' ? (
              <button
                type="button"
                className="action next bonus-continue"
                disabled={isPopupActionPending}
                onClick={async () => {
                  if (isPopupActionPending) return;
                  setIsPopupActionPending(true);
                  try {
                    await openNextPopupOrContinue();
                  } catch (error) {
                    onToast?.(error?.message || '처리에 실패했어요. 잠시 후 다시 시도해주세요.');
                  } finally {
                    setIsPopupActionPending(false);
                  }
                }}
              >
                이어하기
              </button>
            ) : null}
            <button type="button" className="action lobby" disabled={isPopupActionPending} onClick={handleExitToLobby}>
              {isStreakWithQueuedClearPopup
                ? '다음'
                : popupKind === 'timeout' || popupKind === 'click-over'
                  ? '포기하기'
                  : '로비로 돌아가기'}
            </button>
            {isRetryExhaustedState ? <p className="retry-exhausted">추가 도전 기회를 모두 사용했어요.</p> : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}
